import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { BBox, BuildProgress, LatLon, RegionRequest } from '../../shared/types.js';
import { bboxAreaKm2 } from '../../shared/projection.js';
import { clear, el, formatDistance, icon } from './dom.js';
import type { IconName } from './icons.js';
import { MAX_GATES, type CheckTarget, type RaceDraft } from './raceChecks.js';
import { locale, num, plural, serverText, t } from '../i18n/i18n.js';

/**
 * The map a race is drawn on: a box, a start pin and a finish pin, and optionally which way
 * the grid faces and where the checkpoints go.
 *
 * It used to be a self-contained form — search box, toolbar, map, route controls and a status
 * line — that decided for itself what a click meant from what was missing: no start yet, so
 * this click is the start. That is economical and it was the hardest thing in the game to
 * explain, because the same gesture meant four things depending on state you could not see.
 * It is now only the map, and the wizard around it says which *tool* is in hand for the step
 * it is on (`setTool`), so a click on the "start and finish" step places a pin and a click on
 * the "area" step does nothing at all except say so. See DECISIONS.md §63.
 *
 * Tiles come from the public OSM raster service. When they fail — offline, or the tile
 * server is blocked — the map keeps working: panning, the box, the pins and the road
 * preview are all local, so the picker degrades to "drag a box on a blank grid" rather
 * than breaking.
 *
 * The box has draggable corner handles and can be dragged whole, and the pins, gates and the
 * direction arrow are all native `L.marker({ draggable: true })` rather than a plugin or
 * hand-rolled pointer math. Leaflet's own Draggable already disables map panning for the
 * duration of a marker drag and tracks touch the same as mouse; only the gesture that draws or
 * moves the *box itself* is done by hand, because a rectangle's fill isn't a marker and has
 * nothing built in to drag.
 */

export interface PreviewResult {
  ok: boolean;
  error?: string;
  worldId?: string;
  stats?: {
    roadCount: number;
    buildingCount: number;
    areaCount: number;
    junctionCount: number;
    roadMetres: number;
    estimatedTriangles: number;
    generationMs: number;
  };
  payloadBytes?: number;
  routeMetres?: number;
  checkpoints?: number;
  areaKm2?: number;
  preview?: number[][];
  /** The racing line, lat/lon, thinned. Drawn on a tour card as well as on this map. */
  route?: number[];
}

/** The dry run is still going: poll `/api/preview/{job}` for the rest. */
export interface PreviewPending {
  pending: true;
  job: string;
  progress: BuildProgress;
}

/** `/api/estimate`: how much of a box is on the server, and what the rest would cost. */
export interface RegionEstimate {
  tiles: number;
  cached: number;
  /** Null when nothing needs downloading. */
  etaSeconds: number | null;
  offline: boolean;
}

/**
 * What a click or a drag on the map means right now.
 *
 * - `box`: dragging draws a box, or moves the one under the pointer; corners resize it.
 * - `pins`: a click drops whichever pin is armed; pins drag. The map pans.
 * - `route`: the direction arrow drags. Nothing is placed by clicking.
 * - `gates`: as `route`, and a click drops a checkpoint; clicking one removes it.
 * - `view`: look, pan and zoom. Nothing on the map can be changed.
 */
export type PickerTool = 'box' | 'pins' | 'route' | 'gates' | 'view';

export interface RegionPickerOptions {
  maxAreaKm2: () => number;
  /** Anything about the race changed. The wizard re-reads `draft` and re-runs its checks. */
  onChange(): void;
  /** A gesture did nothing, and the person making it deserves to know why. */
  onNotice(message: string): void;
}

export interface Place {
  name: string;
  lat: number;
  lon: number;
  bbox: BBox;
  kind: string;
}

const DEFAULT_CENTER: [number, number] = [50.0614, 19.9366];

/** Long enough that typing "Kraków" is one lookup, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 350;

/**
 * Below this many screen pixels of movement, a mousedown-then-up on the map is a click; at or
 * above it, it is a drag. 6px is comfortably past mouse jitter on a stationary click without
 * making a deliberate small drag feel unresponsive.
 */
const DRAG_THRESHOLD_PX = 6;

type Corner = 'nw' | 'ne' | 'sw' | 'se';
const CORNERS: readonly Corner[] = ['nw', 'ne', 'sw', 'se'];
const OPPOSITE_CORNER: Record<Corner, Corner> = { nw: 'se', ne: 'sw', sw: 'ne', se: 'nw' };

function cornerLatLng(bbox: BBox, corner: Corner): L.LatLng {
  return L.latLng(corner[0] === 'n' ? bbox.north : bbox.south, corner[1] === 'w' ? bbox.west : bbox.east);
}

/** How far from the start pin the direction arrow's handle sits, as a share of the box. */
const HEADING_HANDLE_SPAN = 0.34;

const COMPASS = [t('N'), t('NE'), t('E'), t('SE'), t('S'), t('SW'), t('W'), t('NW')];

/** The eight-point compass name for a bearing — "NE". Shared with the tour builder's rows. */
export function compassPoint(bearing: number): string {
  return COMPASS[((Math.round(bearing / 45) % 8) + 8) % 8];
}

/** "NE 46°" — a bearing said the way somebody reading a north-up map would say it. */
export function bearingLabel(bearing: number): string {
  return `${compassPoint(bearing)} ${Math.round(bearing)}°`;
}

/** In-flight state for a mousedown on the map background — see onGestureStart. */
interface BoxGesture {
  readonly originLatLng: L.LatLng;
  readonly originPoint: L.Point;
  /** Snapshot taken only when the pointer went down inside the existing box, so a
   *  confirmed drag translates the box and everything on it rather than drawing a new one. */
  readonly snapshot: {
    bbox: BBox;
    start: LatLon | null;
    finish: LatLon | null;
    gates: LatLon[];
  } | null;
  confirmed: boolean;
}

export class RegionPicker {
  readonly root: HTMLElement;
  private map: L.Map | null = null;
  private boxLayer: L.Rectangle | null = null;
  private handles: Record<Corner, L.Marker> | null = null;
  private startMarker: L.Marker | null = null;
  private finishMarker: L.Marker | null = null;
  private previewLayer: L.LayerGroup | null = null;
  private bbox: BBox | null = null;
  private start: LatLon | null = null;
  private finish: LatLon | null = null;
  /**
   * The built-in region currently shown, if any.
   *
   * Carried on the emitted request so that adjusting a fixture's race line — turning the
   * grid round, adding a gate — keeps it a fixture, which is what makes it work offline.
   * Dragging or resizing the box drops it, because at that point it is somebody's own
   * region that merely started life at a fixture's coordinates.
   */
  private fixtureId: string | null = null;
  /** Degrees clockwise from north, or null for "whichever way the route leaves". */
  private startBearing: number | null = null;
  private headingMarker: L.Marker | null = null;
  private headingLine: L.Polyline | null = null;
  /** Gates the host placed, in order. Empty means the planner spaces them itself. */
  private gates: LatLon[] = [];
  private gateMarkers: L.Marker[] = [];
  /** How many auto gates to drop, or null for the planner's own count. */
  private gateCount: number | null = null;
  private tool: PickerTool = 'view';
  /**
   * On the box step, whether a drag across the map draws a box or moves the map. Both used
   * to be the same left-button drag, and drawing always won, so the only way to look
   * somewhere else was to zoom out and back in. Starts on `draw` and flips to `pan` once
   * there is a box; Space held, or the right or middle button, always pans.
   */
  private boxGesture: 'draw' | 'pan' = 'draw';
  private spaceHeld = false;
  /** A free drive needs somewhere to start and nowhere to finish. */
  private startOnly = false;
  /** Which pin the next click on the `pins` tool sets. */
  private armed: 'start' | 'finish' | null = 'start';
  private gesture: BoxGesture | null = null;
  /** The corner opposite whichever handle is currently being dragged — it's the anchor
   *  the resize is measured against, and it must not move until the drag ends. */
  private resizeAnchor: L.LatLng | null = null;
  private previewToken = 0;
  /** A view asked for before Leaflet existed — `initMap` applies it instead of the default. */
  private pendingView: (() => void) | null = null;

  private readonly mapNode: HTMLElement;
  private readonly searchBar: HTMLElement;
  private readonly searchInput: HTMLInputElement;
  private readonly searchResults: HTMLElement;
  private searchTimer = 0;
  private searchAbort: AbortController | null = null;
  private searchToken = 0;

  constructor(private readonly options: RegionPickerOptions) {
    this.mapNode = el('div', { class: 'picker-map' });

    this.searchInput = el('input', {
      class: 'search-bar-input',
      type: 'search',
      id: 'picker-search',
      placeholder: t('Search for a city or a street'),
      autocomplete: 'off',
      ariaLabel: t('Search for a place'),
    }) as HTMLInputElement;
    this.searchResults = el('div', { class: 'search-results is-hidden', role: 'listbox' });

    this.searchInput.addEventListener('input', () => this.scheduleSearch());
    this.searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Enter takes the top hit — the common case is "I typed a city, go there".
        const first = this.searchResults.querySelector<HTMLButtonElement>('.search-result');
        if (first) first.click();
        else void this.runSearch();
      } else if (e.key === 'Escape') {
        // Escape belongs to the search box while it has something to close, not the wizard.
        if (!this.searchResults.classList.contains('is-hidden')) e.stopPropagation();
        this.hideResults();
      }
    });

    // The search floats over the top of the map, where every map application has trained
    // people to look for it, and costs the map none of its height.
    this.searchBar = el(
      'div',
      { class: 'search-bar picker-search' },
      el('label', { class: 'search-bar-field', htmlFor: 'picker-search' }, icon('search'), this.searchInput),
      this.searchResults,
    );

    this.root = el('div', { class: 'picker' }, this.mapNode, this.searchBar);

    // Leaflet needs the node in the document with a real size before it initialises.
    queueMicrotask(() => this.initMap(this.mapNode));
  }

  private initMap(node: HTMLElement): void {
    const map = L.map(node, {
      center: DEFAULT_CENTER,
      zoom: 15,
      zoomControl: false,
      attributionControl: true,
    });
    this.map = map;
    // Bottom right, the way every phone map does it, so the search bar has the top edge.
    L.control.zoom({ position: 'bottomright' }).addTo(map);

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors',
      crossOrigin: true,
    }).addTo(map);

    this.previewLayer = L.layerGroup().addTo(map);

    // In the box tool, one gesture has two outcomes, disambiguated by where it starts:
    // dragging from inside the box moves it, dragging from anywhere else draws a new one.
    // Corner handles and pins are separate draggable markers and never reach these handlers.
    map.on('mousedown', (e: L.LeafletMouseEvent) => this.onGestureStart(e));
    map.on('mousemove', (e: L.LeafletMouseEvent) => this.onGestureMove(e));
    map.on('mouseup', () => this.onGestureEnd());
    // Every other tool pans with a drag and acts on a click — and Leaflet does not fire a
    // click at the end of a pan, which is exactly the distinction wanted.
    map.on('click', (e: L.LeafletMouseEvent) => this.onClick(e));
    // Right- and middle-drag pan on every step. Leaflet only ever pans on the left button,
    // and on the box step the left button draws.
    const container = map.getContainer();
    container.addEventListener('mousedown', (e) => this.onPanStart(e));
    container.addEventListener('contextmenu', (e) => e.preventDefault());
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space' && this.tool === 'box' && !isTyping(e)) {
        this.spaceHeld = true;
        container.classList.add('is-space-pan');
      }
    });
    window.addEventListener('keyup', (e) => {
      if (e.code !== 'Space') return;
      this.spaceHeld = false;
      container.classList.remove('is-space-pan');
    });

    // Anything drawn before the map existed — a region loaded while the wizard was still
    // being laid out — goes on now.
    this.redrawAll();
    if (this.pendingView) {
      this.pendingView();
      this.pendingView = null;
    }
    // The picker lives inside a dialog that animates in; without this the map renders
    // into a zero-height box and shows nothing but grey.
    setTimeout(() => map.invalidateSize(), 60);
  }

  // -------------------------------------------------------------------------
  // What the wizard asks of the map
  // -------------------------------------------------------------------------

  /** Switches what a gesture on the map means. See `PickerTool`. */
  setTool(tool: PickerTool): void {
    this.tool = tool;
    if (tool === 'box') {
      this.boxGesture = this.bbox ? 'pan' : 'draw';
      this.root.dataset.boxGesture = this.boxGesture;
    }
    if (tool === 'pins' && !this.armed) this.armed = this.nextPin();
    this.root.dataset.tool = tool;
    this.syncInteractivity();
  }

  /** What a drag across the map does on the box step — see `boxGesture`. */
  setBoxGesture(gesture: 'draw' | 'pan'): void {
    this.boxGesture = gesture;
    this.root.dataset.boxGesture = gesture;
    this.options.onChange();
  }

  get boxGestureMode(): 'draw' | 'pan' {
    return this.boxGesture;
  }

  /**
   * Start pin only, for a free drive: there is no finish to place, and none is sent. The
   * world is still built round a route — the server picks the far end itself — but nobody
   * sees it.
   */
  setStartOnly(on: boolean): void {
    this.startOnly = on;
    if (on) {
      this.finish = null;
      this.finishMarker?.remove();
      this.finishMarker = null;
      if (this.armed === 'finish') this.armed = null;
    }
    this.options.onChange();
  }

  private nextPin(): 'start' | 'finish' | null {
    if (!this.start) return 'start';
    return !this.finish && !this.startOnly ? 'finish' : null;
  }

  /** Which pin the next click places, or null for "neither — drag them instead". */
  armPin(which: 'start' | 'finish' | null): void {
    this.armed = which;
    this.options.onChange();
  }

  get armedPin(): 'start' | 'finish' | null {
    return this.armed;
  }

  setSearchVisible(visible: boolean): void {
    this.searchBar.classList.toggle('is-hidden', !visible);
    if (!visible) this.hideResults();
  }

  focusSearch(): void {
    this.searchInput.focus();
  }

  get draft(): RaceDraft {
    return {
      bbox: this.bbox,
      fixture: this.fixtureId,
      start: this.start,
      finish: this.finish,
      startBearing: this.startBearing,
      gates: [...this.gates],
      gateCount: this.gateCount,
    };
  }

  /**
   * Turns the direction arrow on or off.
   *
   * It starts pointing at the finish pin, which is the direction a host who has not thought
   * about it would have got anyway — so switching it on changes nothing until it is
   * dragged, and dragging it is the whole gesture.
   */
  setHeading(on: boolean): void {
    if (on && !this.start) return;
    if (on === (this.startBearing !== null)) return;
    this.startBearing = on ? (this.finish ? this.bearingTo(this.finish) : 0) : null;
    this.drawHeading();
    this.options.onChange();
  }

  setGateCount(count: number | null): void {
    this.gateCount = count;
    this.options.onChange();
  }

  clearGates(): void {
    this.gates = [];
    this.renderGates();
    this.options.onChange();
  }

  clearPins(): void {
    this.start = null;
    this.finish = null;
    this.startBearing = null;
    this.startMarker?.remove();
    this.startMarker = null;
    this.finishMarker?.remove();
    this.finishMarker = null;
    this.armed = 'start';
    this.drawHeading();
    this.options.onChange();
  }

  /**
   * Shows a region — a built-in one, or a race somebody drew earlier and is now editing.
   *
   * `fixture` is remembered so that everything emitted afterwards is still that built-in
   * region: a host who turns the grid round on Kraków has customised the race, not left the
   * offline-capable region behind for a bounding box that merely matches it.
   */
  load(region: RegionRequest | null, fallback?: { start: LatLon; finish: LatLon }): void {
    this.clearRegion();
    this.previewLayer?.clearLayers();
    if (region) {
      this.bbox = region.bbox;
      this.fixtureId = region.fixture ?? null;
      this.start = region.start ?? fallback?.start ?? null;
      this.finish = region.finish ?? fallback?.finish ?? null;
      this.startBearing = region.startBearing ?? null;
      this.gates = region.checkpoints ? region.checkpoints.map((p) => ({ lat: p.lat, lon: p.lon })) : [];
      this.gateCount = region.checkpoints?.length ? null : (region.checkpointCount ?? null);
      if (this.startOnly) this.finish = null;
      this.armed = this.nextPin();
      this.redrawAll();
      this.fitBox();
    } else {
      this.gateCount = null;
      this.armed = 'start';
    }
    this.options.onChange();
  }

  /** Starts again from nothing, keeping the map where it is. */
  reset(): void {
    this.load(null);
  }

  clearBox(): void {
    this.clearRegion();
    this.previewLayer?.clearLayers();
    this.options.onChange();
  }

  fitBox(): void {
    const bounds = this.boxBounds();
    if (!bounds) return;
    const fit = () => this.map?.fitBounds(bounds, { padding: [48, 48] });
    if (this.map) fit();
    else this.pendingView = fit;
  }

  /**
   * Points at the thing a failed check is about, so the explanation in the panel has a
   * counterpart on the map: a pulse on the box, a pin, or the checkpoints.
   */
  flash(target: CheckTarget): void {
    const nodes: (Element | null | undefined)[] = [];
    if (target === 'box') nodes.push(this.boxLayer?.getElement(), this.bbox ? null : this.mapNode);
    else if (target === 'start') nodes.push(this.startMarker?.getElement() ?? this.mapNode);
    else if (target === 'finish') nodes.push(this.finishMarker?.getElement() ?? this.mapNode);
    else if (target === 'gates') nodes.push(...this.gateMarkers.map((m) => m.getElement()));
    else if (target === 'search') nodes.push(this.searchBar);
    for (const node of nodes) {
      if (!node) continue;
      node.classList.remove('is-flashing');
      // A reflow between remove and add restarts the animation on a second failed press.
      void (node as HTMLElement).getBoundingClientRect?.();
      node.classList.add('is-flashing');
      window.setTimeout(() => node.classList.remove('is-flashing'), 1400);
    }
    if (target === 'box' && this.bbox) this.fitBox();
  }

  // -------------------------------------------------------------------------
  // The box: draw, move, resize
  // -------------------------------------------------------------------------

  private boxBounds(): L.LatLngBounds | null {
    if (!this.bbox) return null;
    return L.latLngBounds([this.bbox.south, this.bbox.west], [this.bbox.north, this.bbox.east]);
  }

  private onGestureStart(e: L.LeafletMouseEvent): void {
    if (!this.map || this.tool !== 'box') return;
    // Leaflet's marker Draggable never stops this event from bubbling to the map — unlike
    // a plugin's edit handles, a plain draggable marker was never built to share a
    // container with another gesture. Without this check, grabbing a handle or a pin
    // starts *both* its own native drag and a box gesture from the same mousedown, and
    // the two fight over `this.bbox` on every subsequent move.
    const target = (e.originalEvent.target as HTMLElement | null)?.closest(
      '.picker-handle, .picker-pin, .picker-gate, .picker-heading-handle, .leaflet-control',
    );
    if (target) return;
    // Only the left button draws. The others pan (see `onPanStart`), and so does the left one
    // in the `pan` mode or with Space held — except from inside the box, which moves it.
    if (e.originalEvent.button !== 0) return;
    const bounds = this.boxBounds();
    const insideBox = bounds !== null && bounds.contains(e.latlng);
    if (!insideBox && (this.boxGesture === 'pan' || this.spaceHeld)) return;
    this.gesture = {
      originLatLng: e.latlng,
      originPoint: this.map.latLngToContainerPoint(e.latlng),
      snapshot:
        insideBox && this.bbox
          ? { bbox: this.bbox, start: this.start, finish: this.finish, gates: [...this.gates] }
          : null,
      confirmed: false,
    };
    this.map.dragging.disable();
    L.DomUtil.addClass(this.map.getContainer(), 'is-dragging-box');
    // Leaflet only hears a release over the map itself, and the search bar and the snackbar
    // float over it from outside. Released over either, the box would follow the pointer
    // until the next click; the window hears it wherever it happens.
    window.addEventListener('mouseup', () => this.onGestureEnd(), { once: true });
  }

  private onPanStart(e: MouseEvent): void {
    if (!this.map || (e.button !== 1 && e.button !== 2)) return;
    // The middle button would otherwise start the browser's autoscroll.
    e.preventDefault();
    const map = this.map;
    let last = { x: e.clientX, y: e.clientY };
    const move = (ev: MouseEvent) => {
      map.panBy([last.x - ev.clientX, last.y - ev.clientY], { animate: false });
      last = { x: ev.clientX, y: ev.clientY };
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  private onGestureMove(e: L.LeafletMouseEvent): void {
    if (!this.map || !this.gesture) return;
    const g = this.gesture;
    if (!g.confirmed) {
      const point = this.map.latLngToContainerPoint(e.latlng);
      if (point.distanceTo(g.originPoint) < DRAG_THRESHOLD_PX) return;
      g.confirmed = true;
      // Proving this is a drag, not a click. Starting outside the box means it's a new
      // one — the old region and its pins are gone the instant the pointer confirms it.
      if (!g.snapshot) this.clearRegion();
    }
    if (g.snapshot) {
      // Moving the box is authoring a region of your own, even if it started at a
      // fixture's coordinates — and the fixture's own bbox is what the server would use.
      this.fixtureId = null;
      const dLat = e.latlng.lat - g.originLatLng.lat;
      const dLon = e.latlng.lng - g.originLatLng.lng;
      this.applyBBox({
        south: g.snapshot.bbox.south + dLat,
        north: g.snapshot.bbox.north + dLat,
        west: g.snapshot.bbox.west + dLon,
        east: g.snapshot.bbox.east + dLon,
      });
      if (g.snapshot.start) this.setPin('start', shift(g.snapshot.start, dLat, dLon));
      if (g.snapshot.finish) this.setPin('finish', shift(g.snapshot.finish, dLat, dLon));
      if (g.snapshot.gates.length) {
        this.gates = g.snapshot.gates.map((p) => shift(p, dLat, dLon));
        this.renderGates();
      }
    } else {
      this.drawBox(g.originLatLng, e.latlng);
    }
  }

  private onGestureEnd(): void {
    if (!this.map || !this.gesture) return;
    const g = this.gesture;
    this.map.dragging.enable();
    L.DomUtil.removeClass(this.map.getContainer(), 'is-dragging-box');
    this.gesture = null;
    // A box is down: the next drag is far more likely to be looking around than starting
    // over, and starting over is one click on "Draw the box" away.
    if (g.confirmed && !g.snapshot && this.bbox) {
      this.boxGesture = 'pan';
      this.root.dataset.boxGesture = 'pan';
    }
    if (!g.confirmed) {
      // A click on this step used to drop a pin when the box was already there, which was
      // never something anybody could have guessed. Now it does nothing, and says so.
      this.options.onNotice(
        this.bbox
          ? t('Drag a corner to resize the box, or drag from inside it to move it.')
          : t('Press and drag across the map to draw a box — a single click does not draw one.'),
      );
      return;
    }
    this.options.onChange();
  }

  private onClick(e: L.LeafletMouseEvent): void {
    const point: LatLon = { lat: e.latlng.lat, lon: e.latlng.lng };
    if (this.tool === 'pins') {
      if (!this.armed) {
        this.options.onNotice(
          t('Both pins are down. Drag one to move it, or choose which one to move and click again.'),
        );
        return;
      }
      const which = this.armed;
      this.setPin(which, point);
      // After the start, the finish; after the finish, nothing — a third click moving a pin
      // nobody chose is the old guessing game again.
      this.armed = which === 'start' && !this.finish && !this.startOnly ? 'finish' : null;
      this.options.onChange();
    } else if (this.tool === 'gates') {
      if (this.gates.length >= MAX_GATES) {
        this.options.onNotice(
          plural(
            MAX_GATES,
            'A race holds at most {n} checkpoints. Click one to remove it.',
            'A race holds at most {n} checkpoints. Click one to remove it.',
          ),
        );
        return;
      }
      this.gates.push(point);
      this.renderGates();
      this.options.onChange();
    }
  }

  private drawBox(a: L.LatLng, b: L.LatLng): void {
    const bounds = L.latLngBounds(a, b);
    this.applyBBox({
      south: bounds.getSouth(),
      west: bounds.getWest(),
      north: bounds.getNorth(),
      east: bounds.getEast(),
    });
  }

  /**
   * The single place that sets `this.bbox`: draws or restyles the rectangle, creates the
   * corner handles the first time there's a box to put them on, and repositions the rest.
   * `skipHandle` leaves the handle currently mid-drag alone — Leaflet is already moving
   * its icon to follow the pointer, and calling `setLatLng` on it too fights that.
   */
  private applyBBox(bbox: BBox, skipHandle?: Corner): void {
    this.bbox = bbox;
    const bounds = L.latLngBounds([bbox.south, bbox.west], [bbox.north, bbox.east]);
    // Colours come from the stylesheet, so the box is drawn in the theme's own primary.
    if (this.boxLayer) this.boxLayer.setBounds(bounds);
    else if (this.map) {
      this.boxLayer = L.rectangle(bounds, { className: 'picker-box', weight: 2, fillOpacity: 0.12 }).addTo(
        this.map,
      );
    }
    this.boxLayer
      ?.getElement()
      ?.classList.toggle('is-over', bboxAreaKm2(bbox) > this.options.maxAreaKm2());
    if (!this.handles) this.createHandles();
    if (this.handles) {
      for (const corner of CORNERS) {
        if (corner === skipHandle) continue;
        this.handles[corner].setLatLng(cornerLatLng(bbox, corner));
      }
    }
    // The arrow's length is a share of the box, so resizing one moves the other.
    this.drawHeading();
    this.options.onChange();
  }

  private createHandles(): void {
    if (!this.map || !this.bbox) return;
    const map = this.map;
    const make = (corner: Corner): L.Marker => {
      const marker = L.marker(cornerLatLng(this.bbox!, corner), {
        icon: L.divIcon({ className: `picker-handle picker-handle-${corner}`, iconSize: [18, 18] }),
        draggable: true,
        keyboard: false,
      }).addTo(map);
      marker.on('dragstart', () => {
        // Resizing makes it your region rather than the built-in one it was cut from.
        this.fixtureId = null;
        this.resizeAnchor = this.bbox ? cornerLatLng(this.bbox, OPPOSITE_CORNER[corner]) : null;
      });
      marker.on('drag', () => {
        if (!this.resizeAnchor) return;
        const bounds = L.latLngBounds(marker.getLatLng(), this.resizeAnchor);
        this.applyBBox(
          {
            south: bounds.getSouth(),
            west: bounds.getWest(),
            north: bounds.getNorth(),
            east: bounds.getEast(),
          },
          corner,
        );
      });
      marker.on('dragend', () => {
        this.resizeAnchor = null;
        this.options.onChange();
      });
      return marker;
    };
    this.handles = { nw: make('nw'), ne: make('ne'), sw: make('sw'), se: make('se') };
    this.syncInteractivity();
  }

  private removeHandles(): void {
    if (!this.handles) return;
    for (const corner of CORNERS) this.handles[corner].remove();
    this.handles = null;
  }

  /**
   * Which markers can be picked up on the current step.
   *
   * Everything stays drawn — seeing the box on the pins step is the point — but only the
   * things the step is about respond, so dragging across a pin on the area step moves the
   * box rather than the pin, and a stray drag on the review step changes nothing at all.
   */
  private syncInteractivity(): void {
    const set = (marker: L.Marker | null | undefined, on: boolean) => {
      if (!marker?.dragging) return;
      if (on) marker.dragging.enable();
      else marker.dragging.disable();
      marker.getElement()?.classList.toggle('is-locked', !on);
    };
    for (const corner of CORNERS) set(this.handles?.[corner], this.tool === 'box');
    this.root.classList.toggle('shows-handles', this.tool === 'box');
    set(this.startMarker, this.tool === 'pins');
    set(this.finishMarker, this.tool === 'pins');
    set(this.headingMarker, this.tool === 'route' || this.tool === 'gates');
    for (const marker of this.gateMarkers) set(marker, this.tool === 'gates');
  }

  // -------------------------------------------------------------------------
  // Pins
  // -------------------------------------------------------------------------

  private setPin(which: 'start' | 'finish', point: LatLon): void {
    if (which === 'start') this.start = point;
    else this.finish = point;
    this.drawPin(which);
    // The arrow is anchored to the start pin, so it goes wherever the pin goes.
    if (which === 'start') this.drawHeading();
  }

  private drawPin(which: 'start' | 'finish'): void {
    if (!this.map) return;
    const point = which === 'start' ? this.start : this.finish;
    if (!point) return;
    const latlng = L.latLng(point.lat, point.lon);
    const existing = which === 'start' ? this.startMarker : this.finishMarker;
    if (existing) {
      existing.setLatLng(latlng);
      return;
    }
    // A map pin rather than a dot: the tip is the place, which is the convention every map
    // has taught everybody, and the head has room for an icon that says which pin it is.
    const marker = L.marker(latlng, {
      icon: L.divIcon({
        className: `picker-pin picker-pin-${which}`,
        iconSize: [34, 42],
        iconAnchor: [17, 40],
      }),
      draggable: true,
      keyboard: false,
    })
      .addTo(this.map)
      .bindTooltip(which === 'start' ? t('Start') : t('Finish'), { direction: 'top', offset: [0, -40] });
    // The glyph goes in as a DOM node rather than as `divIcon`'s html, which is the same rule
    // the rest of the UI follows: nothing in this codebase builds markup from strings.
    marker.getElement()?.append(icon(which === 'start' ? 'play_arrow' : 'sports_score', 'picker-pin-icon'));
    marker.on('drag', () => {
      const ll = marker.getLatLng();
      if (which === 'start') {
        this.start = { lat: ll.lat, lon: ll.lng };
        this.drawHeading();
      } else this.finish = { lat: ll.lat, lon: ll.lng };
    });
    marker.on('dragend', () => this.options.onChange());
    if (which === 'start') this.startMarker = marker;
    else this.finishMarker = marker;
    this.syncInteractivity();
  }

  // -------------------------------------------------------------------------
  // The direction the race sets off in
  // -------------------------------------------------------------------------

  /** Bearing from the start pin to a point, degrees clockwise from north. */
  private bearingTo(point: LatLon): number {
    const start = this.start!;
    // Equirectangular is exact enough over a few kilometres and has no branches; a great
    // circle's initial bearing would differ by less than the arrow's own line width.
    const y = point.lat - start.lat;
    const x = (point.lon - start.lon) * Math.cos((start.lat * Math.PI) / 180);
    if (Math.abs(x) < 1e-12 && Math.abs(y) < 1e-12) return this.startBearing ?? 0;
    return (((Math.atan2(x, y) * 180) / Math.PI) + 360) % 360;
  }

  /** Where the arrow's grab handle sits for the current bearing. */
  private headingHandleLatLng(): L.LatLng | null {
    if (!this.start || this.startBearing === null) return null;
    // Sized off the box, so the arrow stays a comfortable grab at any zoom the region is
    // sensibly viewed at, and falls back to a fixed span before a box exists.
    const spanLat = this.bbox
      ? (this.bbox.north - this.bbox.south) * HEADING_HANDLE_SPAN
      : 0.0025;
    const r = (this.startBearing * Math.PI) / 180;
    const cos = Math.max(0.05, Math.cos((this.start.lat * Math.PI) / 180));
    return L.latLng(
      this.start.lat + spanLat * Math.cos(r),
      this.start.lon + (spanLat * Math.sin(r)) / cos,
    );
  }

  private drawHeading(): void {
    const at = this.headingHandleLatLng();
    if (!this.map || !at || !this.start) {
      this.headingMarker?.remove();
      this.headingMarker = null;
      this.headingLine?.remove();
      this.headingLine = null;
      return;
    }
    const from = L.latLng(this.start.lat, this.start.lon);
    if (this.headingLine) this.headingLine.setLatLngs([from, at]);
    else {
      this.headingLine = L.polyline([from, at], {
        className: 'picker-heading-line',
        weight: 3,
        dashArray: '6 6',
      }).addTo(this.map);
    }
    if (this.headingMarker) this.headingMarker.setLatLng(at);
    else {
      const marker = L.marker(at, {
        icon: L.divIcon({ className: 'picker-heading-handle', iconSize: [30, 30] }),
        draggable: true,
        keyboard: false,
      })
        .addTo(this.map)
        .bindTooltip(t('Drag to turn the grid'), { direction: 'top', offset: [0, -14] });
      marker.getElement()?.append(icon('navigation', 'picker-heading-icon'));
      marker.on('drag', () => {
        const ll = marker.getLatLng();
        this.startBearing = this.bearingTo({ lat: ll.lat, lon: ll.lng });
        // Back onto its circle: the handle sets an angle, not a distance, and letting it
        // wander outwards would read as a second pin somebody had placed.
        const snapped = this.headingHandleLatLng();
        if (snapped) marker.setLatLng(snapped);
        this.drawHeading();
      });
      marker.on('dragend', () => this.options.onChange());
      this.headingMarker = marker;
      this.syncInteractivity();
    }
    // The arrow inside the handle points the way the grid will face.
    const glyph = this.headingMarker.getElement()?.querySelector<SVGElement>('.picker-heading-icon');
    if (glyph) glyph.style.transform = `rotate(${this.startBearing ?? 0}deg)`;
  }

  // -------------------------------------------------------------------------
  // Placed checkpoints
  // -------------------------------------------------------------------------

  /**
   * Redraws every gate rather than the one that changed, because the numbers are the point:
   * removing gate two has to renumber the rest, and a stale "3" beside a gate the race now
   * reaches second is worse than no number at all.
   */
  private renderGates(): void {
    for (const marker of this.gateMarkers) marker.remove();
    this.gateMarkers = [];
    if (this.map) {
      for (const [index, gate] of this.gates.entries()) {
        const marker = L.marker(L.latLng(gate.lat, gate.lon), {
          icon: L.divIcon({ className: 'picker-gate', iconSize: [26, 26] }),
          draggable: true,
          keyboard: false,
        })
          .addTo(this.map)
          .bindTooltip(t('Checkpoint {n} — click to remove', { n: index + 1 }), { direction: 'top', offset: [0, -12] });
        const node = marker.getElement();
        if (node) node.textContent = String(index + 1);
        marker.on('drag', () => {
          const ll = marker.getLatLng();
          this.gates[index] = { lat: ll.lat, lon: ll.lng };
        });
        marker.on('dragend', () => this.options.onChange());
        marker.on('click', () => {
          if (this.tool !== 'gates') return;
          this.gates.splice(index, 1);
          this.renderGates();
          this.options.onChange();
        });
        this.gateMarkers.push(marker);
      }
    }
    this.syncInteractivity();
  }

  // -------------------------------------------------------------------------
  // Place search
  // -------------------------------------------------------------------------

  private scheduleSearch(): void {
    window.clearTimeout(this.searchTimer);
    if (this.searchInput.value.trim().length < 2) {
      this.hideResults();
      return;
    }
    this.searchTimer = window.setTimeout(() => void this.runSearch(), SEARCH_DEBOUNCE_MS);
  }

  private async runSearch(): Promise<void> {
    const query = this.searchInput.value.trim();
    if (query.length < 2) return;

    // Only the newest keystroke's results may render; a slow earlier request must not
    // overwrite a fast later one.
    const token = ++this.searchToken;
    this.searchAbort?.abort();
    const abort = new AbortController();
    this.searchAbort = abort;

    this.renderResults([], t('Searching…'));
    try {
      const res = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`, {
        signal: abort.signal,
      });
      const body = (await res.json()) as { ok: boolean; error?: string; places?: Place[] };
      if (token !== this.searchToken) return;
      const places = body.places ?? [];
      if (!body.ok) this.renderResults([], body.error ? serverText(body.error) : t('Place search failed.'));
      else if (!places.length) this.renderResults([], t('Nothing found for “{query}”.', { query }));
      else this.renderResults(places);
    } catch (err) {
      if ((err as Error).name === 'AbortError' || token !== this.searchToken) return;
      this.renderResults([], t('Place search is unavailable here — pan and zoom the map instead.'));
    }
  }

  private renderResults(places: Place[], message?: string): void {
    clear(this.searchResults);
    this.searchResults.classList.remove('is-hidden');
    if (message) {
      this.searchResults.append(el('div', { class: 'search-result-note' }, message));
      return;
    }
    for (const place of places) {
      const button = el(
        'button',
        { class: 'search-result', type: 'button', role: 'option' },
        icon(placeIcon(place.kind)),
        el(
          'span',
          { class: 'search-result-text' },
          el('span', { class: 'search-result-name' }, place.name),
          el('span', { class: 'search-result-kind' }, place.kind.replace(/_/g, ' ')),
        ),
      );
      button.addEventListener('click', () => this.goTo(place));
      this.searchResults.append(button);
    }
  }

  private hideResults(): void {
    this.searchResults.classList.add('is-hidden');
    clear(this.searchResults);
  }

  private goTo(place: Place): void {
    this.hideResults();
    this.searchInput.value = place.name.split(',')[0];
    if (!this.map) return;
    const bounds = L.latLngBounds(
      [place.bbox.south, place.bbox.west],
      [place.bbox.north, place.bbox.east],
    );
    // A city's own bounds are hundreds of times the race cap, so fitting them exactly
    // would leave the host looking at a 4 km² box the size of a full stop. Cap the zoom
    // out at roughly "you can see a few streets".
    this.map.fitBounds(bounds, { padding: [16, 16], maxZoom: 15 });
  }

  /**
   * Takes the visible map rectangle as the region, shrunk about its centre if it is over
   * the cap. Dragging an exact box on a slippy map is fiddly; zooming until the streets
   * you want fill the frame is not.
   */
  useCurrentView(): void {
    if (!this.map) return;
    const b = this.map.getBounds();
    let bbox: BBox = {
      south: b.getSouth(),
      west: b.getWest(),
      north: b.getNorth(),
      east: b.getEast(),
    };
    // A little in from the edges, so the corner handles are on screen to be grabbed rather
    // than sitting under the frame of the map.
    bbox = scaleBox(bbox, 0.86);
    const area = bboxAreaKm2(bbox);
    const max = this.options.maxAreaKm2();
    if (area > max) bbox = scaleBox(bbox, Math.sqrt(max / area) * 0.98);
    // Taking the whole visible rectangle is the same move as dragging a fresh box — any
    // pins from a previous region would likely land outside it.
    this.clearRegion();
    this.applyBBox(bbox);
    this.fitBox();
  }

  // -------------------------------------------------------------------------
  // The preview
  // -------------------------------------------------------------------------

  /**
   * Draws the road network the server says this region contains, and the racing line it
   * planned through it.
   *
   * The line is the only honest answer to "did my direction and my gates do anything" —
   * the arrow and the numbered pins say what was *asked for*, and the route the server came
   * back with says what the streets allowed.
   */
  showPreview(result: PreviewResult): void {
    if (!this.map || !this.previewLayer) return;
    this.previewLayer.clearLayers();
    const token = ++this.previewToken;
    if (!result.ok || !result.preview) return;
    for (const line of result.preview) {
      const points: [number, number][] = [];
      for (let i = 0; i < line.length; i += 2) points.push([line[i], line[i + 1]]);
      L.polyline(points, { className: 'picker-road-line', weight: 1.5 }).addTo(this.previewLayer);
    }
    const route = result.route ?? [];
    if (route.length >= 4) {
      const points: [number, number][] = [];
      for (let i = 0; i < route.length; i += 2) points.push([route[i], route[i + 1]]);
      L.polyline(points, { className: 'picker-route-line', weight: 5 }).addTo(this.previewLayer);
    }
    if (token !== this.previewToken) this.previewLayer.clearLayers();
  }

  clearPreview(): void {
    this.previewToken++;
    this.previewLayer?.clearLayers();
  }

  /** The numbers a dry run came back with, said the way somebody choosing a race would. */
  static stats(result: PreviewResult): { label: string; value: string }[] {
    if (!result.ok || !result.stats) return [];
    const s = result.stats;
    const mb = (result.payloadBytes ?? 0) / 1024 / 1024;
    // Rough but honest: generation is measured, transfer assumes a slowish connection.
    const estimatedLoad = s.generationMs / 1000 + mb / 1.5 + 0.4;
    return [
      { label: t('Race distance'), value: formatDistance(result.routeMetres ?? 0) },
      { label: t('Checkpoints'), value: String(result.checkpoints ?? 0) },
      { label: t('Streets'), value: s.roadCount.toLocaleString(locale()) },
      { label: t('Buildings'), value: s.buildingCount.toLocaleString(locale()) },
      { label: t('Download'), value: `${num(mb, 1)} MB` },
      { label: t('Loads in about'), value: `${num(estimatedLoad, 1)} s` },
    ];
  }

  // -------------------------------------------------------------------------

  private redrawAll(): void {
    if (!this.map) return;
    if (this.bbox) this.applyBBox(this.bbox);
    this.drawPin('start');
    this.drawPin('finish');
    this.drawHeading();
    this.renderGates();
  }

  private clearRegion(): void {
    this.bbox = null;
    this.start = null;
    this.finish = null;
    this.fixtureId = null;
    this.startBearing = null;
    this.gates = [];
    this.armed = 'start';
    this.boxLayer?.remove();
    this.boxLayer = null;
    this.startMarker?.remove();
    this.startMarker = null;
    this.finishMarker?.remove();
    this.finishMarker = null;
    this.drawHeading();
    this.renderGates();
    this.removeHandles();
  }

  region(): RegionRequest | null {
    if (!this.bbox) return null;
    return {
      bbox: this.bbox,
      ...(this.start ? { start: roundPoint(this.start) } : {}),
      ...(this.finish && !this.startOnly ? { finish: roundPoint(this.finish) } : {}),
      ...(this.startBearing !== null ? { startBearing: round1(this.startBearing) } : {}),
      // Placed gates win: the count is not offered while there are any, and sending both
      // would leave the server to guess which of the two the host meant.
      ...(this.startOnly
        ? {}
        : this.gates.length
          ? { checkpoints: this.gates.map(roundPoint) }
          : this.gateCount !== null
            ? { checkpointCount: this.gateCount }
            : {}),
      seed: 1,
      ...(this.fixtureId ? { fixture: this.fixtureId } : {}),
    };
  }

  invalidateSize(): void {
    this.map?.invalidateSize();
  }

  dispose(): void {
    this.map?.remove();
    this.map = null;
  }
}

function shift(point: LatLon, dLat: number, dLon: number): LatLon {
  return { lat: point.lat + dLat, lon: point.lon + dLon };
}

function scaleBox(bbox: BBox, scale: number): BBox {
  const midLat = (bbox.south + bbox.north) / 2;
  const midLon = (bbox.west + bbox.east) / 2;
  const halfLat = ((bbox.north - bbox.south) / 2) * scale;
  const halfLon = ((bbox.east - bbox.west) / 2) * scale;
  return { south: midLat - halfLat, north: midLat + halfLat, west: midLon - halfLon, east: midLon + halfLon };
}

/** A city reads as a city and a street as a street, at a glance down the results. */
function placeIcon(kind: string): IconName {
  if (/road|street|highway|way|residential|square/i.test(kind)) return 'route';
  return 'location_on';
}

/** The bearing goes into a URL-ish request and a cache key; a tenth of a degree is plenty. */
function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** ~11 cm of latitude — finer than the road a pin or a gate is about to snap to. */
function roundPoint(p: LatLon): LatLon {
  return { lat: Math.round(p.lat * 1e6) / 1e6, lon: Math.round(p.lon * 1e6) / 1e6 };
}

/** Space in a text box is a space, not a pan. */
function isTyping(e: KeyboardEvent): boolean {
  const target = e.target as HTMLElement | null;
  return target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
}
